import classNames from 'classnames/bind'
import { useTranslation } from 'react-i18next'

import PlainDateMonthSwitcher from 'app/common/ui-next/plain-date-month-picker'
import PlainDateSwitcher from 'app/common/ui-next/plain-date-switcher'
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue'
import * as dateTypes from 'app/common/constants/dateTypes'
import useFromTo from 'app/common/hooks/useFromTo'
import { useCurrencySign } from 'app/common/components-next/currency'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent, Header2 } from 'app/common/templates-next/page-template'
import SimpleDate from 'app/common/classes/simple-date'

import { PurchasesApi, statuses } from '../PurchasesApi'
import PurchasesTable from './PurchasesTable'
import selectTotal from './selectTotal'
import styles from './CafeteriaPurchases.module.scss';

const cx = classNames.bind(styles)

type Props = {
  date: SimpleDate
  dateType: 'month' | 'day'
  header?: string
  person: {
    id: number
    name: {
      first: string
      last: string
      middle: string
      gender: 'male' | 'female' | 'androgynous'
    }
    timezone: string
  }
  onChangeDate: (date: string, dateFormat: string, daily?: boolean) => void
  hideSomeColumns?: boolean
}

const CafeteriaPurchases = ({
  date,
  dateType: dt,
  header = 'Покупки',
  person: { id: personId, timezone },
  onChangeDate,
  hideSomeColumns,
}: Props) => {
  const { t } = useTranslation('cafeteria')
  const currency = useCurrencySign()
  const dateString = date.getStringFormatted(dt === 'day' ? 'YYYY-MM-DD' : 'YYYY-MM')

  const dateType = dt === 'day' ? dateTypes.DAYS : dateTypes.MONTHS // length(split('-', date)) // 2 - мясяцы, 3 - дни

  const [from, to] = useFromTo({ date: dateString, dateType })

  return (
    <PurchasesApi personId={personId} from={from} to={to} status={statuses.SPENT} autoRequest>
      {({ content: { purchases }, loading }) => {
        const total = selectTotal(purchases)

        return (
          <>
            <Sticky>
              {(ref, sticky) => (
                <PageTemplateContent reference={ref} className={cx({ sticky })} flex skipPadding>
                  <Header2 className={styles.header} skipMargin>
                    {header}
                  </Header2>
                  <Header2 className={cx(styles.total, { loading })} skipMargin wide={false}>
                    {`${t('purchases_total', 'Итого')}: ${toFixedValueCurrency(total, false, currency)}`}
                  </Header2>
                  <PlainDateSwitcher
                    className={styles.dateSwitcher}
                    date={dateString}
                    dateFormat={dateType === dateTypes.MONTHS ? 'YYYY-MM' : 'YYYY-MM-DD'}
                    displayDateFormat={dateType === dateTypes.MONTHS ? 'MMMM' : undefined}
                    stepType={dt}
                    onDateChange={onChangeDate}
                  >
                    <PlainDateMonthSwitcher
                      date={dateString}
                      dateFormat={dateType === dateTypes.MONTHS ? 'YYYY-MM' : 'YYYY-MM-DD'}
                      onChangeDate={onChangeDate}
                      monthsAmount={24}
                    />
                  </PlainDateSwitcher>
                </PageTemplateContent>
              )}
            </Sticky>
            <PurchasesTable timezone={timezone} hideSomeColumns={hideSomeColumns} />
          </>
        )
      }}
    </PurchasesApi>
  )
}

export default CafeteriaPurchases
